import { useShallow } from 'zustand/react/shallow';

import { MediaAssetType } from '@/types/media';
import { Product } from '@/types/product';

import { useCurrentApp } from '@/stores/userDataStore';
import { useStudioVideoStore } from '@/features/ai-video-creator/stores/studioVideoStore';
import * as MediaAssetAPI from '@/api/media-asset';
import { reactQueryClient } from '@/api/react-query/query-client';

import { ReadonlyURLSearchParams, useSearchParams } from 'next/navigation';
import { getOutputBucketUrl } from '@/utils/public-urls';
import { MediaAssetProviders, ProductMediaAsset } from '../stores/productMediaSlice';
import { getRecommendedMedia } from '@/api/avatarVideoTemplate';
import { useCallback } from 'react';

export const getPreselectedMediaAssets = async ({
  appKey,
  searchParams,
}: {
  appKey: string;
  searchParams: ReadonlyURLSearchParams;
}): Promise<ProductMediaAsset[]> => {
  const assetId = searchParams.get('assetId');
  const assetType = searchParams.get('assetType') as MediaAssetType;
  const assetSrc = searchParams.get('assetSrc');
  const assetProvider = searchParams.get('assetProvider');

  if ((!assetId && !assetSrc) || !assetType) {
    return [];
  }

  if (assetSrc) {
    const mediaAsset = {
      id: assetId || assetSrc,
      src: assetSrc!,
      type: assetType!,
      provider: assetProvider!,
      settings: assetType === MediaAssetType.video ? { duration: 5000 } : undefined,
    };
    return [mediaAsset];
  }

  const { mediaAsset } = await reactQueryClient.fetchQuery({
    queryKey: ['preselected-media-assets', assetId],
    queryFn: async () =>
      MediaAssetAPI.getMediaAssetById({
        appKey,
        id: assetId!,
        type: assetType! as MediaAssetType,
      }),
    staleTime: Infinity,
  });

  return [
    {
      ...mediaAsset,
      provider: MediaAssetProviders.aiGenerated,
      settings:
        mediaAsset.type === MediaAssetType.video && mediaAsset.durationMS
          ? { duration: mediaAsset.durationMS }
          : undefined,
      src: `${getOutputBucketUrl()}/${mediaAsset.result}`,
    },
  ];
};

export const useProductMedia = () => {
  const { appKey, appUrl } = useCurrentApp();
  const searchParams = useSearchParams();
  const { setProductMedia, setIsProductMediaLoading } = useStudioVideoStore(useShallow((state) => state));

  const prefetchProductMedia = useCallback(
    async (product: Product) => {
      try {
        setIsProductMediaLoading(true);
        const [recommendedMedia, preselectedMedia] = await Promise.all([
          getRecommendedMedia({
            appKey,
            appUrl,
            productId: product.productId!,
          }),
          getPreselectedMediaAssets({ appKey, searchParams }),
        ]);
        setProductMedia([...recommendedMedia.mediaAssets, ...preselectedMedia]);
      } catch (error) {
        console.error('Error prefetching product media', error);
      } finally {
        setIsProductMediaLoading(false);
      }
    },
    [setProductMedia, setIsProductMediaLoading]
  );

  return {
    prefetchProductMedia,
  };
};
